import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useDataSource } from '../api/data-source';
import { tenantHeader, unwrap } from '../api/http';
import { usePrincipal } from '../auth/AuthBoundary';
import { grantFor } from './support-view';

/** The hover text on a control a read-only view disables. */
export const READ_ONLY_HINT = 'Read-only view';

/**
 * Whether this is Paige staff looking at a company under a read-only support grant (round 3 of the
 * customer page's critique, support B1). The API is the truth: `/me` with the company named answers
 * the grant's mode (the contract's StaffGrantView), and refuses every write anyway with 403
 * `support_grant_read_only`. Until it answers, the grant this tab opened says, and a staff session
 * in a company it is not a member of is read-only, which is every grant's default. A member is never
 * read-only. Screens only disable their writes with it; nothing is hidden or moved.
 */
export function useReadOnlyView(): boolean {
  const { tenant } = useParams({ strict: false }) as { tenant?: string };
  const principal = usePrincipal();
  const ds = useDataSource();
  const outsider = Boolean(
    tenant && principal.staff && !principal.tenants.some((t) => t.id === tenant),
  );
  const answer = useQuery({
    queryKey: ['me', 'support-grant', tenant],
    queryFn: async () =>
      unwrap(await ds.client.GET('/me', { headers: tenantHeader(tenant ?? '') })).supportGrant ??
      null,
    enabled: outsider,
    staleTime: 60_000,
    retry: false,
  });
  if (!outsider || !tenant) return false;
  if (answer.data !== undefined) return answer.data?.mode !== 'write';
  return grantFor(tenant)?.mode !== 'write';
}
